/* eslint-disable react/no-unstable-nested-components */
import { FC, useMemo, useState } from 'react';
// Input.Search has no in-app equivalent — Base/Input exports Input / Password /
// TextArea / Phone / RichText, but no search variant. Keep antd here.
import { Input, Space } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { ENotificationType, useShowToastNotification } from '@packages/utils';
import Button from 'components/Base/Button/Button';
import { Select } from 'components/Base/Select';
import { Tooltip } from 'components/Base/Tooltip';
import { EmptyView } from 'components/Base/EmptyView';
import { EIconNames } from 'components/Base/Icon';
import { Table } from 'components/Base/Table';
import {
  ETableStoragePaths,
  TColumnType,
} from 'components/Base/Table/Table.types';
import CardContent from 'components/Base/Layout/Content/CardContent';
import { useCheckPermission } from 'hooks/useCheckPermission';
import {
  EDuplicateEntityType,
  EDuplicateStatus,
  TDuplicatePairUI,
} from 'data/types/duplicateManagement.types';
import {
  useSearchDuplicatesQuery,
  useTriggerDetectionMutation,
} from 'redux/apiSlice/duplicateManagement.slice';
import { SStatusBadge, SToolbar } from '../../DuplicateMerge.styles';
import DuplicatePairCompareRow from '../DuplicatePairCompareRow';

type TDuplicatePairListProps = {
  entityType: EDuplicateEntityType;
};

const DEFAULT_PAGE_SIZE = 20;

const STATUS_FILTER_OPTIONS: {
  value: EDuplicateStatus | 'ALL';
  label: string;
}[] = [
  { value: 'ALL', label: 'All statuses' },
  { value: EDuplicateStatus.UNREVIEWED, label: 'Not Reviewed' },
  { value: EDuplicateStatus.MERGED, label: 'Merged' },
];

// Only UNREVIEWED + MERGED are surfaced in the UI. IN_REVIEW is an internal
// BE transition state, and DISMISSED is intentionally hidden per Confluence
// §3.6 / Q10 (the dismiss endpoint is preserved server-side, but the FE
// does not expose "Dismissed" anywhere as a user-facing label).
const statusBadge = (status: EDuplicateStatus) => {
  const cfg: Partial<
    Record<EDuplicateStatus, { label: string; tone: string }>
  > = {
    [EDuplicateStatus.UNREVIEWED]: { label: 'Not Reviewed', tone: 'blue' },
    [EDuplicateStatus.MERGED]: { label: 'Merged', tone: 'green' },
  };
  const c = cfg[status];
  if (!c) return null;
  return <SStatusBadge $tone={c.tone}>{c.label}</SStatusBadge>;
};

const recordDisplayName = (pair: TDuplicatePairUI, side: 'A' | 'B'): string => {
  const name = side === 'A' ? pair.recordADisplayName : pair.recordBDisplayName;
  if (name) return name;
  // BE returns null when the underlying record was hard-deleted. Fall back to
  // a short ID so the row is still actionable.
  const id = side === 'A' ? pair.recordAId : pair.recordBId;
  return `Record ${id.slice(0, 8)}…`;
};

const DuplicatePairList: FC<TDuplicatePairListProps> = ({ entityType }) => {
  const canDetect = useCheckPermission('DUPLICATE_MANAGEMENT_DETECT');
  const { showToastNotification } = useShowToastNotification();

  // Local query/filter state. BE search request uses 0-indexed pages; Base/Table
  // also takes a 0-indexed `currentPage` so no off-by-one shim is needed.
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<EDuplicateStatus | 'ALL'>(
    'ALL',
  );

  const { data, isFetching } = useSearchDuplicatesQuery({
    entityType,
    page,
    size: pageSize,
    ...(query ? { query } : {}),
    ...(statusFilter !== 'ALL' ? { status: statusFilter } : {}),
    sortBy: 'detectedAt',
    sortDirection: 'desc',
  });

  const [triggerDetection, { isLoading: detecting }] =
    useTriggerDetectionMutation();

  const handleRunDetection = async () => {
    try {
      const response = await triggerDetection({ entityType }).unwrap();
      if (response.status === 'accepted') {
        showToastNotification({
          message:
            response.message ||
            `Scanning ${
              entityType === EDuplicateEntityType.PEOPLE
                ? 'People'
                : 'Organization'
            } records for duplicates.`,
          type: ENotificationType.SUCCESS,
        });
      } else {
        showToastNotification({
          message: response.message || `Status: ${response.status}.`,
          type: ENotificationType.WARNING,
        });
      }
    } catch (err) {
      showToastNotification({
        message: 'Could not start detection',
        type: ENotificationType.ERROR,
      });
    }
  };

  const hasActiveFilters = query !== '' || statusFilter !== 'ALL';

  const columns: TColumnType<TDuplicatePairUI>[] = useMemo(
    () => [
      {
        title: 'Record 1 (Primary)',
        key: 'record1',
        render: (_v, row) => recordDisplayName(row, 'A'),
      },
      {
        title: 'Record 2',
        key: 'record2',
        render: (_v, row) => recordDisplayName(row, 'B'),
      },
      {
        title: 'Status',
        key: 'status',
        width: 180,
        render: (_v, row) => statusBadge(row.status),
      },
    ],
    [],
  );

  const storagePath =
    entityType === EDuplicateEntityType.PEOPLE
      ? ETableStoragePaths.DUPLICATE_PAIRS_PEOPLE
      : ETableStoragePaths.DUPLICATE_PAIRS_ORGANIZATION;

  const tableHeaderActions = (
    <Space key="filters">
      <Input.Search
        placeholder="Search by record name…"
        allowClear
        defaultValue={query}
        onSearch={value => {
          setQuery(value.trim());
          setPage(0);
        }}
        style={{ width: 260 }}
      />
      <Select
        value={statusFilter}
        onChange={value => {
          setStatusFilter(value as EDuplicateStatus | 'ALL');
          setPage(0);
        }}
        options={STATUS_FILTER_OPTIONS}
        style={{ width: 160 }}
      />
      <Tooltip
        title={
          canDetect ? '' : 'Requires DUPLICATE_MANAGEMENT_DETECT permission'
        }
      >
        <Button
          icon={<ReloadOutlined />}
          variant="primary"
          onClick={handleRunDetection}
          disabled={!canDetect}
          loading={detecting}
        >
          Run detection
        </Button>
      </Tooltip>
    </Space>
  );

  return (
    <CardContent>
      <SToolbar>{tableHeaderActions}</SToolbar>
      <Table<TDuplicatePairUI>
        rowKey="id"
        columns={columns}
        dataSource={data?.duplicates || []}
        loading={isFetching}
        currentPage={page}
        pageSize={pageSize}
        setPageSize={size => {
          setPageSize(size);
          setPage(0);
        }}
        totalDataCount={data?.totalElements || 0}
        onChange={pagination => {
          if (pagination.current && pagination.current - 1 !== page) {
            setPage(pagination.current - 1);
          }
        }}
        storagePath={storagePath}
        hideActions
        tabHeight={44}
        locale={{
          emptyText: (
            <EmptyView
              alt="no duplicates"
              emptyText={
                hasActiveFilters
                  ? 'No duplicates match the current filters.'
                  : 'No duplicates detected yet.'
              }
              icon={EIconNames.EMPTY_DATA}
            />
          ),
        }}
        expandable={{
          rowExpandable: row =>
            row.status === EDuplicateStatus.UNREVIEWED ||
            row.status === EDuplicateStatus.IN_REVIEW,
          expandedRowRender: row => <DuplicatePairCompareRow pair={row} />,
        }}
      />
    </CardContent>
  );
};

export default DuplicatePairList;
